import { useState } from 'react';
import type { BBSPost, BBSUser, BBSReply } from '../types';
import { MOCK_USERS } from '../types';
import { useLang } from '../../../hooks/useLang';

interface PostDetailProps {
  post: BBSPost;
  currentUser: BBSUser | null;
  onClose: () => void;
  onReply: (postId: string, content: string) => void;
  onRequireAuth: () => void;
}

const POST_DETAIL_LABELS: Record<string, Record<string, string>> = {
  pinned: { zh: '置顶', en: 'Pinned', fr: 'Épinglé', es: 'Fijado', pt: 'Fixado', ja: '固定', de: 'Angepinnt', ko: '고정', ar: 'مثبت', ru: 'Закреплено' },
  views: { zh: '浏览', en: 'Views', fr: 'Vues', es: 'Vistas', pt: 'Visualizações', ja: '閲覧', de: 'Aufrufe', ko: '조회', ar: 'مشاهدات', ru: 'Просмотры' },
  likes: { zh: '点赞', en: 'Likes', fr: 'J\'aime', es: 'Me gusta', pt: 'Curtidas', ja: 'いいね', de: 'Gefällt mir', ko: '좋아요', ar: 'إعجابات', ru: 'Нравится' },
  replies: { zh: '回复', en: 'Replies', fr: 'Réponses', es: 'Respuestas', pt: 'Respostas', ja: '返信', de: 'Antworten', ko: '답글', ar: 'ردود', ru: 'Ответы' },
  attachments: { zh: '附件', en: 'Attachments', fr: 'Pièces jointes', es: 'Adjuntos', pt: 'Anexos', ja: '添付ファイル', de: 'Anhänge', ko: '첨부파일', ar: 'مرفقات', ru: 'Вложения' },
  uploadedAt: { zh: '上传于', en: 'Uploaded', fr: 'Téléchargé', es: 'Subido', pt: 'Enviado', ja: 'アップロード', de: 'Hochgeladen', ko: '업로드됨', ar: 'تم الرفع', ru: 'Загружено' },
  replyTitle: { zh: '回复', en: 'Replies', fr: 'Réponses', es: 'Respuestas', pt: 'Respostas', ja: '返信', de: 'Antworten', ko: '답글', ar: 'ردود', ru: 'Ответы' },
  noReplies: { zh: '暂无回复，来抢沙发～', en: 'No replies yet, be the first!', fr: 'Pas encore de réponses, soyez le premier!', es: 'Aún no hay respuestas, ¡sé el primero!', pt: 'Ainda sem respostas, seja o primeiro!', ja: 'まだ返信がありません、最初の返信者になりましょう！', de: 'Noch keine Antworten, sei der Erste!', ko: '아직 답글이 없습니다, 첫 번째가 되세요!', ar: 'لا توجد ردود بعد، كن الأول!', ru: 'Пока нет ответов, будьте первым!' },
  replyPlaceholder: { zh: '发表你的看法...', en: 'Share your thoughts...', fr: 'Partagez votre avis...', es: 'Comparte tu opinión...', pt: 'Compartilhe sua opinião...', ja: 'あなたの考えを共有...', de: 'Teilen Sie Ihre Gedanken...', ko: '의견을 공유하세요...', ar: 'شارك رأيك...', ru: 'Поделитесь своим мнением...' },
  submitReply: { zh: '发表回复', en: 'Post Reply', fr: 'Publier', es: 'Publicar', pt: 'Publicar', ja: '返信を投稿', de: 'Antworten', ko: '답글 작성', ar: 'نشر رد', ru: 'Ответить' },
  loginToReply: { zh: '登录后发表回复', en: 'Login to reply', fr: 'Connectez-vous pour répondre', es: 'Inicie sesión para responder', pt: 'Faça login para responder', ja: 'ログインして返信', de: 'Anmelden zum Antworten', ko: '답글을 작성하려면 로그인하세요', ar: 'سجل الدخول للرد', ru: 'Войдите, чтобы ответить' },
};

const getUser = (id: string) => MOCK_USERS.find((u) => u.id === id) || MOCK_USERS[0];
const FILE_ICONS: Record<string, string> = {
  pdf: 'ri-file-pdf-2-line',
  doc: 'ri-file-word-line',
  docx: 'ri-file-word-line',
  xlsx: 'ri-file-excel-line',
  xls: 'ri-file-excel-line',
  ppt: 'ri-file-ppt-line',
  pptx: 'ri-file-ppt-line',
};
const FILE_COLORS: Record<string, string> = {
  pdf: '#DC2626',
  doc: '#1D4ED8',
  docx: '#1D4ED8',
  xlsx: '#059669',
  xls: '#059669',
  ppt: '#EA580C',
  pptx: '#EA580C',
};

export default function PostDetail({ post, currentUser, onClose, onReply, onRequireAuth }: PostDetailProps) {
  const [replyText, setReplyText] = useState('');
  const author = getUser(post.authorId);
  const { lang } = useLang();
  const getLabel = (key: string) => POST_DETAIL_LABELS[key]?.[lang] ?? POST_DETAIL_LABELS[key]?.['en'] ?? key;

  const handleReply = () => {
    if (!currentUser) {
      onRequireAuth();
      return;
    }
    if (!replyText.trim()) return;
    onReply(post.id, replyText);
    setReplyText('');
  };

  return (
    <div className="fixed inset-0 z-[99998] flex items-start justify-center p-4 pt-20 overflow-y-auto">
      <div className="absolute inset-0 bg-black/50 backdrop-blur-sm" onClick={onClose} />
      <div className="relative bg-white rounded-2xl shadow-2xl w-full max-w-3xl mb-8">
        {/* Header */}
        <div className="flex items-start justify-between p-6 border-b border-gray-100">
          <div className="flex-1 min-w-0 pr-4">
            <div className="flex items-center gap-2 mb-2 flex-wrap">
              {post.pinned && (
                <span className="px-2 py-0.5 bg-red-50 text-red-500 text-[10px] font-bold rounded border border-red-200">📌 {getLabel('pinned')}</span>
              )}
              {post.tags.map((tag) => (
                <span key={tag} className="px-2 py-0.5 bg-[#7C3AED]/10 text-[#7C3AED] text-[10px] rounded-full">{tag}</span>
              ))}
            </div>
            <h2 className="font-black text-lg text-gray-900">{post.title}</h2>
            <div className="flex items-center gap-3 mt-2 text-xs text-gray-400 flex-wrap">
              <span className="flex items-center gap-1">
                <div className="w-5 h-5 rounded-full bg-[#7C3AED] flex items-center justify-center text-white text-[10px] font-bold">{author.avatar}</div>
                <span className="font-semibold text-gray-600">{author.username}</span>
              </span>
              <span>{post.createdAt}</span>
              <span className="flex items-center gap-1"><i className="ri-eye-line" />{post.views} {getLabel('views')}</span>
              <span className="flex items-center gap-1"><i className="ri-heart-line" />{post.likes} {getLabel('likes')}</span>
              <span className="flex items-center gap-1"><i className="ri-chat-3-line" />{post.replies.length} {getLabel('replies')}</span>
            </div>
          </div>
          <button onClick={onClose} className="w-8 h-8 flex items-center justify-center text-gray-400 hover:text-gray-700 cursor-pointer transition-colors shrink-0">
            <i className="ri-close-line text-xl" />
          </button>
        </div>

        {/* Content */}
        <div className="p-6">
          <div className="prose prose-sm max-w-none text-gray-700 text-sm leading-relaxed whitespace-pre-wrap">
            {post.content}
          </div>

          {/* Attachments */}
          {post.attachments.length > 0 && (
            <div className="mt-6">
              <h4 className="font-bold text-gray-700 text-sm mb-3 flex items-center gap-2">
                <i className="ri-attachment-2 text-[#7C3AED]" />
                {getLabel('attachments')}（{post.attachments.length}）
              </h4>
              <div className="space-y-2">
                {post.attachments.map((att) => {
                  const ext = att.name.split('.').pop()?.toLowerCase() || 'pdf';
                  return (
                    <div
                      key={att.id}
                      className="flex items-center gap-3 p-3 bg-gray-50 rounded-xl border border-gray-200 hover:border-[#7C3AED]/30 transition-all group cursor-pointer"
                    >
                      <div className="w-8 h-8 flex items-center justify-center rounded-lg" style={{ background: `${FILE_COLORS[ext] || '#6B7280'}20` }}>
                        <i className={`${FILE_ICONS[ext] || 'ri-file-line'} text-base`} style={{ color: FILE_COLORS[ext] || '#6B7280' }} />
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="text-sm font-semibold text-gray-700 truncate group-hover:text-[#7C3AED] transition-colors">{att.name}</div>
                        <div className="text-xs text-gray-400">{att.size} · {getLabel('uploadedAt')} {att.uploadedAt}</div>
                      </div>
                      <div className="w-7 h-7 flex items-center justify-center text-gray-400 group-hover:text-[#7C3AED] transition-colors">
                        <i className="ri-download-2-line text-sm" />
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* Replies */}
          <div className="mt-8">
            <h4 className="font-bold text-gray-800 text-sm mb-4 flex items-center gap-2">
              <i className="ri-chat-3-line text-[#7C3AED]" />
              {getLabel('replyTitle')}（{post.replies.length}）
            </h4>
            {post.replies.length === 0 ? (
              <div className="text-center py-6 text-gray-400 text-sm bg-gray-50 rounded-xl">{getLabel('noReplies')}</div>
            ) : (
              <div className="space-y-4">
                {post.replies.map((reply: BBSReply, idx: number) => {
                  const replyAuthor = getUser(reply.authorId);
                  return (
                    <div key={reply.id} className="flex gap-3">
                      <div className="w-8 h-8 rounded-full bg-[#7C3AED]/20 flex items-center justify-center text-[#7C3AED] font-black text-sm shrink-0">
                        {replyAuthor.avatar}
                      </div>
                      <div className="flex-1 bg-gray-50 rounded-xl p-3">
                        <div className="flex items-center justify-between mb-1">
                          <span className="text-xs font-bold text-gray-700">{replyAuthor.username}</span>
                          <span className="text-[10px] text-gray-400">{reply.createdAt}</span>
                        </div>
                        <p className="text-sm text-gray-700 leading-relaxed">{reply.content}</p>
                        <div className="flex items-center gap-1 mt-2">
                          <button className="flex items-center gap-1 text-gray-400 hover:text-red-400 transition-colors cursor-pointer text-xs">
                            <i className="ri-heart-line text-xs" />
                            {reply.likes}
                          </button>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}

            {/* Reply Input */}
            <div className="mt-6">
              {currentUser ? (
                <div className="flex gap-3">
                  <div className="w-8 h-8 rounded-full bg-[#7C3AED] flex items-center justify-center text-white font-black text-sm shrink-0">
                    {currentUser.avatar}
                  </div>
                  <div className="flex-1">
                    <textarea
                      value={replyText}
                      onChange={(e) => setReplyText(e.target.value)}
                      placeholder={getLabel('replyPlaceholder')}
                      rows={3}
                      maxLength={500}
                      className="w-full border border-gray-200 rounded-xl px-4 py-3 text-sm focus:outline-none focus:border-[#7C3AED] resize-none transition-colors"
                    />
                    <div className="flex items-center justify-between mt-2">
                      <span className="text-xs text-gray-400">{replyText.length}/500</span>
                      <button
                        onClick={handleReply}
                        disabled={!replyText.trim()}
                        className="px-4 py-2 bg-[#7C3AED] text-white text-xs font-bold rounded-lg hover:opacity-90 transition-opacity cursor-pointer disabled:opacity-40 whitespace-nowrap"
                      >
                        {getLabel('submitReply')}
                      </button>
                    </div>
                  </div>
                </div>
              ) : (
                <button
                  onClick={onRequireAuth}
                  className="w-full py-3 border-2 border-dashed border-gray-200 rounded-xl text-gray-400 text-sm hover:border-[#7C3AED]/40 hover:text-[#7C3AED] transition-all cursor-pointer"
                >
                  <i className="ri-login-circle-line mr-2" />
                  {getLabel('loginToReply')}
                </button>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
